export interface AccurateSerialFormatItem {
  itemNo?: string;
  rowCount: number;
}

export interface AccurateSerialFormatOptions {
  now?: Date;
  transDate?: string;
  index?: number;
  digitCount?: number;
  uuidFragment?: string;
  incrementStart?: number;
}

const formatDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}${month}${date.getFullYear()}`;
};

const normalizeDigitCount = (value?: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 5;
};

const randomUuidFragment = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().replace(/-/g, "").slice(0, 6).toUpperCase();
  }

  return Math.random().toString(16).slice(2, 8).toUpperCase();
};

const parseTransactionDate = (value?: string): Date | null => {
  const match = value?.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;

  const date = new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
  return date.getFullYear() === Number(match[3]) &&
    date.getMonth() === Number(match[2]) - 1 &&
    date.getDate() === Number(match[1])
    ? date
    : null;
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const getNextSerialIncrement = (
  serialNumbers: string[],
  sequenceKey?: string,
): number => {
  const values = serialNumbers.flatMap((serialNumber) => {
    const trimmed = serialNumber.trim();
    const match = sequenceKey
      ? trimmed.match(new RegExp(`^${escapeRegExp(sequenceKey)}-(\\d+)$`))
      : trimmed.match(/-(\d+)$/);
    return match ? [Number(match[1])] : [];
  });

  return values.length === 0 ? 1 : Math.max(...values) + 1;
};

export const getSerialSequenceKey = (
  components: string[],
  item: AccurateSerialFormatItem,
  options: AccurateSerialFormatOptions = {},
): string => {
  const incrementIndex = components.indexOf("[INCREMENT]");
  const scopeComponents = incrementIndex >= 0
    ? components.slice(0, incrementIndex)
    : components;

  return buildSerialFormatPreview(scopeComponents, item, options);
};

const formatTransactionDate = (day: number, month: number, year: number): string =>
  `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;

export const getSerialQueryRange = (
  components: string[],
  transDate?: string,
): { fromDate?: string; toDate?: string } => {
  const date = parseTransactionDate(transDate);
  if (!date) return {};

  if (components.includes("[DATE]")) {
    return { fromDate: transDate, toDate: transDate };
  }

  const year = date.getFullYear();
  if (components.includes("[YEAR]") && components.includes("[MONTH]")) {
    return {
      fromDate: formatTransactionDate(1, date.getMonth() + 1, year),
      toDate: formatTransactionDate(new Date(year, date.getMonth() + 1, 0).getDate(), date.getMonth() + 1, year),
    };
  }

  if (components.includes("[YEAR]")) {
    return {
      fromDate: formatTransactionDate(1, 1, year),
      toDate: formatTransactionDate(31, 12, year),
    };
  }

  return {};
};

export const buildSerialFormatPreview = (
  components: string[],
  item: AccurateSerialFormatItem,
  options: AccurateSerialFormatOptions = {},
): string => {
  const now = parseTransactionDate(options.transDate) || options.now || new Date();
  const index = options.index || 0;
  const increment = options.incrementStart === undefined
    ? String(index + 1).padStart(4, "0")
    : String(options.incrementStart + index);
  const digitCount = normalizeDigitCount(options.digitCount);
  const randomNumber = String(Date.now() + index)
    .slice(-digitCount)
    .padStart(digitCount, "0");
  const uuid = options.uuidFragment || randomUuidFragment();

  return components
    .map((component) => {
      switch (component) {
        case "[CODE]":
          return item.itemNo || "";
        case "[DATE]":
          return formatDate(now);
        case "[YEAR]":
          return String(now.getFullYear());
        case "[MONTH]":
          return String(now.getMonth() + 1).padStart(2, "0");
        case "[INCREMENT]":
          return increment;
        case "[UUID]":
          return uuid;
        case "[RANDOM_NUMBER]":
          return randomNumber;
        default:
          return component;
      }
    })
    .filter(Boolean)
    .join("-");
};

export const generateAccurateSerials = (
  components: string[],
  item: AccurateSerialFormatItem,
  options: AccurateSerialFormatOptions = {},
): string[] =>
  Array.from({ length: Math.max(0, item.rowCount) }, (_, index) =>
    buildSerialFormatPreview(components, item, { ...options, index }),
  );
